import React, { useCallback, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'querystring';
import { useDispatch, useSelector } from 'react-redux';
import { mergeQueries, useInfinityLoader, useQueryParams } from 'hooks';
import { getErrorFromStates, isFetchingStates } from 'store/utils';
import { redirectTo, routes } from 'utils/routing';
import { MARKETS_SLUGS_MAP } from 'utils/transform';

import { EventsSelectors, MarketsSelectors } from 'store/selectors';
import { EventsActions, MarketsActions } from 'store/actions';

import { UserFooter } from 'components/common/Footer';
import { UserHeader } from 'components/common/Header';
import EventsScreen from 'components/screens/EventsScreen';
import { PromoPrograms } from 'components/promo/EventsPage';
import FilterPanel from 'components/filters/FilterPanel';
import SortFilter from 'components/filters/SortFilter';
import FacebookPixel from 'components/common/FacebookPixel';
import { TabTitle } from 'components/common/TabTitle';
import RenderComponent from 'components/common/RenderComponent';
import { makeStyles } from 'tss-react/mui';
import { parseRelativeUrl } from 'next/dist/shared/lib/router/utils/parse-relative-url';
import { useTranslation } from 'i18n';
import { NoSsr } from '@mui/material';
import withGetInitialPropsRouteValidation from './withGetInitialPropsRouteValidation';
import withRouteValidation from './withRouteValidation';
import { trackPage } from '../components/screens/ScrenTracker';
import PromoProgramsPicture from '../components/promo/EventsPage/PromoProgramsPicture';
import EventRootTypesFilter from '../components/filters/EventTypesFilter';
import EventTopicsFilter from '../components/filters/EventTopicsFilter';
import EventTargetAgesFilter from '../components/filters/EventTargetAgesFilter';
import PriceFilter from '../components/filters/PriceFilter';
import DatesFilter from '../components/filters/DatesFilter';

type PromoPageParams = {
  hiddenQueryParams: ParsedUrlQueryInput;
  defaultQueryParams: ParsedUrlQueryInput;
  queryParams: ParsedUrlQueryInput;
  filters: any;
  language?: string;
};

interface PromoPageArgs {
  titleKey?: string;
  marketTextKey?: string;
  backgroundPosition?: string;
  pageName: string;
  mainPromoPicture?: boolean;
  getFilters?: (market: string) => ParsedUrlQueryInput;
  filters?: ParsedUrlQueryInput;
  getImageSrc: (params: PromoPageParams) => string;
  getPixelId?: (params: PromoPageParams) => number;
  getTitleKey?: (market: string) => string;
  imageMarginBottom?: number;
  noFilters?: boolean;
  noMarket?: boolean;
  titleStyle?: any;
  onlyLocal?: boolean;
  imageMaxWidth?: number;
}

interface PromoPageProps {
  hiddenQueryParams: ParsedUrlQueryInput;
  defaultQueryParams: ParsedUrlQueryInput;
  filters: any;
}

const querySchema = Yup.object({
  marketSlug: Yup.mixed().oneOf(['detroit', 'montreal', 'cincinnati']).required(),
});

const useStyles = makeStyles()((theme) => ({
  eventsScreen: {
    [theme.breakpoints.up('md')]: {
      marginTop: 8,
    },
  },
}));

const withMarketPromoPage = (args: PromoPageArgs) => {
  const PromoPage: NextPage<PromoPageProps, any> = (props) => {
    const { hiddenQueryParams, defaultQueryParams, filters } = props;
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const router = useRouter();
    const [queryParams] = useQueryParams();

    const [, i18n] = useTranslation();
    const { language } = i18n;

    const marketsState = useSelector(MarketsSelectors.marketsState);
    const market = marketsState.entities.find((market) => market.slug === router.query.marketSlug);

    const pixelId =
      args.getPixelId &&
      args.getPixelId({
        hiddenQueryParams,
        defaultQueryParams,
        queryParams,
        filters,
      });

    const handleToggleFavoriteEvent = useCallback(
      (eventId: any) => dispatch(EventsActions.toggleFavoriteEvent.request({ id: eventId })),
      [dispatch],
    );

    const stateSelector = useMemo(() => EventsSelectors.upcomingEventsState(), []);
    const paginationSelector = useMemo(() => EventsSelectors.upcomingEventsPagination(), []);
    const {
      state: eventsState,
      setElementWrapper,
      setElementChild,
    } = useInfinityLoader({
      actions: EventsActions.getUpcomingEvents,
      stateSelector,
      paginationSelector,
      options: {
        skipInitialLoad: true,
        hiddenQueryParams,
        defaultQueryParams,
        queryParams,
      },
    });

    useEffect(() => {
      window.scrollTo(0, 0);
      setElementWrapper(window);
      setElementChild(document.documentElement);
      trackPage(args.pageName);
    }, [setElementChild, setElementWrapper]);

    const title = () => (args.getTitleKey ? args.getTitleKey(market.slug) : args.titleKey);

    const pageFilters = () => {
      const visibleFilters = [];
      if (args.noFilters) return [];
      if (!filters.type) visibleFilters.push(<EventRootTypesFilter disabled={eventsState.isFetching} />);
      if (!(filters.category || filters.context === 'adult_learning_page'))
        visibleFilters.push(<EventTopicsFilter disabled={eventsState.isFetching} />);
      if (!(filters.target_age || filters.target_age_slug || filters.context === 'adult_learning_page'))
        visibleFilters.push(<EventTargetAgesFilter disabled={eventsState.isFetching} />);
      if (!filters.price) visibleFilters.push(<PriceFilter disabled={eventsState.isFetching} />);
      if (!(filters.start_at__date__gte || filters.start_at__date__lte))
        visibleFilters.push(<DatesFilter disabled={eventsState.isFetching} />);

      return visibleFilters;
    };

    return (
      <NoSsr>
        {market && <TabTitle i18nKey={`tab.${args.titleKey}`} ns="public" signLabel="general" market={market.title} />}
        <UserHeader />
        {args.mainPromoPicture ? (
          <PromoProgramsPicture
            src={args.getImageSrc({ hiddenQueryParams, defaultQueryParams, queryParams, filters, language })}
            backgroundPosition="full"
          />
        ) : (
          <RenderComponent
            isLoading={isFetchingStates(marketsState)}
            errors={getErrorFromStates(marketsState)}
            isFetched={Boolean(market)}
            renderContent={() => (
              <PromoPrograms
                titleKey={`promo.${title()}`}
                backgroundPosition={args.backgroundPosition}
                market={market.title}
                marketTextKey={args.marketTextKey}
                src={args.getImageSrc({ hiddenQueryParams, defaultQueryParams, queryParams, filters, language })}
                imageMarginBottom={args.imageMarginBottom}
                imageMaxWidth={args.imageMaxWidth}
                titleStyle={args.titleStyle}
              />
            )}
          />
        )}
        <RenderComponent isFetched={Boolean(pixelId)} renderContent={() => <FacebookPixel pixelId={pixelId} />} />
        <div className={classes.eventsScreen}>
          <EventsScreen
            events={eventsState.entities}
            isEventsFetching={eventsState.isFetching}
            onToggleFavoriteEvent={handleToggleFavoriteEvent}
            top={
              <FilterPanel sort={<SortFilter defaultOrder="session_date" disabled={eventsState.isFetching} />}>
                {pageFilters()}
              </FilterPanel>
            }
          />
        </div>
        <UserFooter />
      </NoSsr>
    );
  };

  // todo change to getServerSide
  PromoPage.getInitialProps = withGetInitialPropsRouteValidation(
    querySchema,
    async ({ res, asPath, query, store }: any) => {
      const { query: queryParams } = parseRelativeUrl(asPath);
      const { marketSlug } = query;
      const { ordering } = queryParams;
      const filters = args.getFilters ? args.getFilters(marketSlug) : args.filters;

      if (marketSlug && typeof marketSlug === 'string') {
        const defaultQueryParams = {
          ordering: ordering && typeof ordering === 'string' ? ordering : 'session_date',
        };

        const hiddenQueryParams = {
          ...filters,
          ...(args.onlyLocal
            ? { market_slug: marketSlug }
            : !args.noMarket && { market_slug: MARKETS_SLUGS_MAP[marketSlug as keyof typeof MARKETS_SLUGS_MAP] }),
          page_size: 12,
        };

        const params: any = mergeQueries(defaultQueryParams, queryParams, hiddenQueryParams);

        store.dispatch(EventsActions.getUpcomingEvents.reset({ params }));
        store.dispatch(EventsActions.getUpcomingEvents.request({ params }));
        store.dispatch(MarketsActions.getMarkets.request());

        return {
          hiddenQueryParams,
          defaultQueryParams,
          filters,
        };
      }

      return redirectTo(res, routes.events());
    },
  );

  return withRouteValidation(PromoPage);
};

export default withMarketPromoPage;
